import { Search as SearchIcon } from '@mui/icons-material';
import { Autocomplete, Box, Input, Theme, useTheme } from '@mui/material';
import { useRef } from 'react';

import { useDebouncedCallback } from '@/hooks';
import { AnalyticsService } from '@/services';
import { appStyles } from '@/theme';

export interface SearchBarComponentProps {
  onSearch: (value: string) => void;
  onUpdateQuerySuggestions?: (value: string) => void;
  onIconClick?: () => void;
  onFocus?: () => void;
  initialValue?: string;
  querySuggestions?: string[];
  placeholder?: string;
  fullWidth?: boolean;
  expandable?: boolean;
  darkMode?: boolean;
}

export const SearchBarComponent = ({
  onSearch,
  onUpdateQuerySuggestions = () => {},
  onIconClick,
  onFocus,
  initialValue,
  querySuggestions = [],
  placeholder = 'Search',
  fullWidth = false,
  expandable = false,
  darkMode = false,
}: SearchBarComponentProps) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebouncedCallback(onSearch, 500);
  const debouncedUpdateQuerySuggestions = useDebouncedCallback(
    onUpdateQuerySuggestions,
    200,
  );

  /* onInputChange is triggered every time the content of the input field changes */
  const onInputChange = (_e: React.SyntheticEvent, value: string) => {
    debouncedSearch(value);
    debouncedUpdateQuerySuggestions(value);
  };

  /* onChange is only triggered for Autocomplete events and has more detailed info */
  const onChange = (
    _e: React.SyntheticEvent,
    value: string | { label: string; position: number } | null,
    reason: string,
  ) => {
    if (reason === 'selectOption' && typeof value !== 'string' && !!value) {
      AnalyticsService.searchSelectQuerySuggestion({
        searchTerm: inputRef.current?.value ?? '',
        selectedQuerySuggestion: value.label,
        selectedQueryPosition: value.position,
      });
    }
  };

  const handleIconClick = () => {
    onIconClick?.();
    setTimeout(() => {
      inputRef?.current?.focus();
    }, 0);
  };

  const width = fullWidth ? '100%' : 220;

  return (
    <Autocomplete
      disablePortal
      options={querySuggestions.map((suggestion, index) => ({
        label: suggestion,
        position: index,
      }))}
      freeSolo
      openOnFocus
      defaultValue={initialValue ? { label: initialValue, position: 0 } : null}
      onChange={onChange}
      onInputChange={onInputChange}
      onFocus={onFocus}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          onSearch((e.target as HTMLInputElement).value);
        }
      }}
      slotProps={{
        listbox: {
          sx: {
            p: 1,
            m: 0,
            '& .MuiAutocomplete-option': {
              ...theme.typography.body1,
              p: 1,
              borderRadius: '5px',
            },
            '& .MuiAutocomplete-option.Mui-focused, .MuiAutocomplete-option.Mui-focusVisible':
              {
                ...theme.typography.body1SemiBold,
                backgroundColor: 'purple25',
                color: 'secondary.main',
              },
          },
        },
        paper: {
          sx: {
            ...appStyles.popoverPaper,
            mt: 0.5,
            '&:empty': {
              border: 'none',
            },
          },
        },
      }}
      renderInput={params => (
        <Box
          ref={params.InputProps.ref}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width,
          }}>
          <Input
            disableUnderline
            sx={[
              styles.searchInput,
              expandable
                ? styles.expandableSearchInput
                : styles.constantSearchInput,
              darkMode &&
                (expandable
                  ? styles.darkMode.expandableSearchInput
                  : styles.darkMode.searchInput),
            ]}
            startAdornment={<SearchIcon onClick={handleIconClick} />}
            inputRef={inputRef}
            placeholder={placeholder}
            type="search"
            {...params}
          />
        </Box>
      )}
    />
  );
};

const styles = {
  searchInput: {
    height: 48,
    input: {
      color: 'text.main',
      '&::-webkit-search-cancel-button': {
        WebkitAppearance: 'none',
        height: 16,
        width: 16,
        cursor: 'pointer',
        backgroundColor: 'currentColor',
        maskImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path d='M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/></svg>\")",
      },
    },
    transition: '0.5s all ease',
    fontSize: 15,
    borderRadius: 6,
    px: 1,
    '&>svg': {
      mx: 1,
    },
  },
  expandableSearchInput: {
    width: 30,
    px: 0,
    '&>svg': ({ palette }: Theme) => ({
      transition: '0.5s all ease',
      color: palette.header.icon,
      cursor: 'pointer',
      ml: 0,
    }),
    '&.Mui-focused': {
      px: 1,
      width: '100%',
    },
  },
  constantSearchInput: {
    width: '100%',
    backgroundColor: 'grey50',
  },
  darkMode: {
    searchInput: {
      color: 'text.contrast',
      input: {
        color: 'text.contrast',
      },
      bgcolor: 'rgba(255, 255, 255, 0.2)',
      '&.Mui-focused': {
        bgcolor: 'rgba(255, 255, 255, 0.2)',
      },
    },
    expandableSearchInput: {
      bgcolor: 'transparent',
      input: {
        color: 'text.contrast',
      },
      '&.Mui-focused': {
        '&>svg': {
          color: 'text.contrast',
        },
        bgcolor: { xs: 'grey200', md: 'rgba(255, 255, 255, 0.2)' },
      },
    },
  },
};
